import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";

type CouponsContextType = {
  coupons: number;
  redeemCoupons: (amount: number) => { ok: true } | { ok: false; reason: "not_authenticated" | "invalid_amount" | "insufficient_coupons" };
  addCoupons: (amount: number) => { ok: true } | { ok: false; reason: "not_authenticated" | "invalid_amount" };
};

const CouponsContext = createContext<CouponsContextType | undefined>(undefined);

const STORAGE_PREFIX = "mutzur_coupons_";
const DEFAULT_CUSTOMER_COUPONS = 4;

function getStorageKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}`;
}

export function CouponsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [coupons, setCoupons] = useState(0);

  useEffect(() => {
    if (!user) {
      setCoupons(0);
      return;
    }

    const key = getStorageKey(user.id);
    const raw = localStorage.getItem(key);

    if (raw === null) {
      const initial = user.role === "customer" ? DEFAULT_CUSTOMER_COUPONS : 0;
      localStorage.setItem(key, String(initial));
      setCoupons(initial);
      return;
    }

    const parsed = Number(raw);
    setCoupons(Number.isFinite(parsed) ? parsed : 0);
  }, [user?.id, user?.role]);

  const redeemCoupons: CouponsContextType["redeemCoupons"] = (amount) => {
    if (!user) return { ok: false, reason: "not_authenticated" };
    if (!Number.isFinite(amount) || amount <= 0) {
      return { ok: false, reason: "invalid_amount" };
    }
    if (coupons < amount) return { ok: false, reason: "insufficient_coupons" };

    const next = coupons - amount;
    localStorage.setItem(getStorageKey(user.id), String(next));
    setCoupons(next);
    return { ok: true };
  };

  const addCoupons: CouponsContextType["addCoupons"] = (amount) => {
    if (!user) return { ok: false, reason: "not_authenticated" };
    if (!Number.isFinite(amount) || amount <= 0) {
      return { ok: false, reason: "invalid_amount" };
    }

    const next = coupons + amount;
    localStorage.setItem(getStorageKey(user.id), String(next));
    setCoupons(next);
    return { ok: true };
  };

  return (
    <CouponsContext.Provider value={{ coupons, redeemCoupons, addCoupons }}>
      {children}
    </CouponsContext.Provider>
  );
}

export function useCoupons() {
  const context = useContext(CouponsContext);
  if (context === undefined) {
    throw new Error("useCoupons must be used within a CouponsProvider");
  }
  return context;
}
